// Serviço da tela Automações (TXM-400).
//
// Período em DIAS (AAAA-MM-DD), não mês. tipo_fluxo é obrigatório no E-Social
// (RUBRICAS | XML_PERIODO | FOLHA); E-Social manda lote_dias, SPED manda sped_tipo.
import api from "./api-planejamento-tributario.service";
import { listarEmpresas } from "./api-empresas.service";

const soDigitos = (v) => (v || "").replace(/\D/g, "");
// Raiz do CNPJ (8 primeiros dígitos) = matriz/filiais do mesmo estabelecimento,
// diferente de "grupo econômico" (vínculo comercial manual, sem relação societária).
const raizCnpj = (cnpj) => soDigitos(cnpj).slice(0, 8);

// Busca via api-empresas (Cadastro de Empresas), cacheada em memória. Sem filtro
// de texto na API, então buscamos a lista uma vez e filtramos client-side.
let _empresasReais = null;
function carregarEmpresasReais() {
  if (!_empresasReais) {
    _empresasReais = listarEmpresas({ limit: 1000, offset: 0 })
      .then((resp) => {
        const lista = resp?.dados != null ? resp.dados : Array.isArray(resp) ? resp : [];
        return lista.map((e) => ({
          cnpj: soDigitos(e.cnpj),
          razaoSocial: e.razao_social || e.razaoSocial || e.nome_empresarial || "",
          ativo: String(e.status ?? "ATIVO").toUpperCase() !== "INATIVO",
          idGrupoEconomico: e.id_grupo_economico ?? null,
          isMatriz: !!e.is_matriz,
        }));
      })
      .catch((err) => {
        console.error("[automacoes] api-empresas indisponível:", err?.message || err);
        _empresasReais = null; // permite retry na próxima busca
        throw new Error("Não foi possível carregar as empresas. Tente novamente.");
      });
  }
  return _empresasReais;
}

// Razão social por CNPJ, para a tela trocar o CNPJ que a API grava no lugar do nome quando a busca
// dela falha. Consulta pontual por empresa (e não a lista de 1000 acima): o Cadastro passa de 1000
// empresas em prod e quem ficava fora da primeira leva nunca apareceria (mesmo motivo do 43f7ed5).
// Guarda a PROMESSA por CNPJ: chamadas simultâneas e recargas da lista não repetem a consulta. O que a
// api respondeu (inclusive "não achei") fica em cache; erro de rede não, para tentar de novo depois.
const _razaoPorCnpj = new Map();
const PARALELO = 5;
const MAX_CONSULTAS = 50;

async function buscarRazaoSocialDoCnpj(cnpj) {
  // `cnpj` é o filtro da tela Prévia INSS; `q` é a busca do autocomplete. Só vale resposta com CNPJ
  // IDÊNTICO: se a api ignorar o filtro e devolver "as primeiras empresas", isso nunca vira nome errado.
  for (const filtro of [{ cnpj }, { q: cnpj }]) {
    const resp = await listarEmpresas({ ...filtro, limit: 5, offset: 0, _ts: Date.now() });
    const lista = resp?.dados != null ? resp.dados : Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
    const achada = lista.find((e) => soDigitos(e?.cnpj) === cnpj);
    const nome = achada?.razao_social || achada?.razaoSocial || achada?.nome_empresarial;
    if (nome) return nome;
  }
  return "";
}

/**
 * @param {string[]} cnpjs CNPJs (com ou sem máscara) cujo nome falta
 * @returns {Promise<Map<string,string>>} CNPJ só com dígitos -> razão social, só dos que foram achados
 */
export async function resolverRazoesSociais(cnpjs) {
  const unicos = [...new Set((cnpjs || []).map(soDigitos).filter((c) => c.length === 14))];
  if (unicos.length > MAX_CONSULTAS) {
    console.warn(`[automacoes] ${unicos.length} CNPJs sem razão social; só os ${MAX_CONSULTAS} primeiros foram consultados.`);
  }
  const alvo = unicos.slice(0, MAX_CONSULTAS);

  const nomes = new Map();
  for (let i = 0; i < alvo.length; i += PARALELO) {
    await Promise.all(
      alvo.slice(i, i + PARALELO).map(async (cnpj) => {
        if (!_razaoPorCnpj.has(cnpj)) {
          const consulta = buscarRazaoSocialDoCnpj(cnpj);
          _razaoPorCnpj.set(cnpj, consulta);
          consulta.catch(() => _razaoPorCnpj.delete(cnpj));
        }
        try {
          const nome = await _razaoPorCnpj.get(cnpj);
          if (nome) nomes.set(cnpj, nome);
        } catch (err) {
          console.warn(`[automacoes] razão social do CNPJ ${cnpj} indisponível:`, err?.message || err);
        }
      })
    );
  }
  return nomes;
}

/** Autocomplete de empresa por razão social ou CNPJ (>= 2 caracteres, até 10). Busca real no servidor. */
export async function buscarEmpresasAutomacoes(termo) {
  const t = (termo || "").trim();
  if (t.length < 2) return [];
  let resp;
  try {
    // _ts: cache-busting — usuário digita rápido (02. -> 02.500 -> ...), e alguma camada de
    // cache (navegador ou o próprio api-empresas) devolveu 304/corpo antigo pra uma URL nova,
    // mostrando "Empresa não encontrada" pra CNPJ que existe (reproduzido ao vivo 11/08/2026).
    // URL sempre única por busca => nunca bate em cache de ninguém.
    resp = await listarEmpresas({ q: t, limit: 10, offset: 0, _ts: Date.now() });
  } catch (err) {
    console.error("[automacoes] api-empresas indisponível:", err?.message || err);
    throw new Error("Não foi possível carregar as empresas. Tente novamente.");
  }
  const lista = resp?.dados != null ? resp.dados : Array.isArray(resp) ? resp : [];
  return lista
    .map((e) => ({
      cnpj: soDigitos(e.cnpj),
      razaoSocial: e.razao_social || e.razaoSocial || e.nome_empresarial || "",
      ativo: String(e.status ?? "ATIVO").toUpperCase() !== "INATIVO",
      idGrupoEconomico: e.id_grupo_economico ?? null,
      isMatriz: !!e.is_matriz,
    }))
    .filter((e) => e.ativo);
}

/**
 * Estabelecimentos (matriz + filiais) do mesmo CNPJ raiz que `empresa` — refinamento
 * por estabelecimento do SPED Fiscal (TXM-352). Fonte primária: o catálogo já
 * cacheado (carregarEmpresasReais); se a empresa tiver `idGrupoEconomico`, também
 * busca esse grupo e junta os resultados (dedup por CNPJ).
 */
export async function buscarEstabelecimentosRelacionados(empresa) {
  const raiz = raizCnpj(empresa?.cnpj);
  if (raiz.length !== 8) return [];

  const mapear = (e) => ({
    cnpj: soDigitos(e.cnpj),
    razaoSocial: e.razao_social || e.razaoSocial || e.nome_empresarial || "",
    isMatriz: !!(e.is_matriz ?? e.isMatriz),
    uf: e.uf || "",
  });

  const todas = await carregarEmpresasReais();
  const doCache = todas.filter((e) => e.ativo !== false && raizCnpj(e.cnpj) === raiz).map(mapear);

  let doGrupo = [];
  if (empresa?.idGrupoEconomico) {
    try {
      const resp = await listarEmpresas({ id_grupo_economico: empresa.idGrupoEconomico, limit: 500, offset: 0 });
      const lista = resp?.dados != null ? resp.dados : Array.isArray(resp) ? resp : [];
      doGrupo = lista.map(mapear).filter((e) => raizCnpj(e.cnpj) === raiz);
    } catch {
      // best-effort — o cache já cobre o caso comum
    }
  }

  const porCnpj = new Map();
  for (const e of [...doCache, ...doGrupo]) {
    if (e.cnpj.length === 14) porCnpj.set(e.cnpj, e);
  }
  return [...porCnpj.values()].sort((a, b) => (b.isMatriz - a.isMatriz) || a.cnpj.localeCompare(b.cnpj));
}

/**
 * Empresas cadastradas no grupo econômico `idGrupo` (lista CRUA da api-empresas). O vínculo vem do
 * Cadastro de Empresas (id_grupo_economico) — o mesmo que as telas de cadastro já usam. Diferente de
 * buscarEstabelecimentosRelacionados, NÃO filtra por raiz de CNPJ: grupo econômico são raízes
 * diferentes. Quem normaliza/ordena é grupoEconomico.util (puro, testável).
 * Erros sobem pro chamador decidir a UI.
 */
export async function listarEmpresasDoGrupo(idGrupo) {
  if (!idGrupo) return [];
  const resp = await listarEmpresas({ id_grupo_economico: idGrupo, limit: 500, offset: 0 });
  return resp?.dados != null ? resp.dados : Array.isArray(resp) ? resp : [];
}

/** Monta o payload exato que o backend espera — contrato de alinhamento com o back. */
export function montarPayloadSolicitacao(input) {
  const base = {
    cnpj: soDigitos(input.cnpj),
    tipo: input.tipo, // "esocial" | "sped"
    periodo_inicio: input.periodoInicio,
    periodo_fim: input.periodoFim,
  };
  if (input.tipo === "esocial") {
    return { ...base, tipo_fluxo: input.tipoFluxo, lote_dias: input.loteDias };
  }
  // cnpjEstabelecimento = refinamento por estabelecimento (só SPED Fiscal, TXM-352).
  if (input.spedTipo === "fiscal" && input.cnpjEstabelecimento) {
    return {
      ...base,
      sped_tipo: input.spedTipo,
      todos_estabelecimentos: false,
      cnpj_estabelecimento: soDigitos(input.cnpjEstabelecimento),
    };
  }
  return { ...base, sped_tipo: input.spedTipo };
}

/** Lista as solicitações reais, persistidas no banco do backend feeder. */
export async function listarSolicitacoesAutomacoes(filtros = {}) {
  const params = {};
  if (filtros.tipo) params.tipo = filtros.tipo;
  if (filtros.status) params.status = filtros.status;
  if (filtros.cnpj) params.cnpj = filtros.cnpj;
  const data = await api.get("/planejamento/automacoes/solicitacoes", { params, timeout: 20_000 });
  return Array.isArray(data?.solicitacoes) ? data.solicitacoes : [];
}

/**
 * Cria a solicitação: chama o backend feeder, que enfileira o RPA na central-rpas.
 * @param {object} input
 * @param {{signal?: AbortSignal}} [options] - signal permite cancelar o request.
 */
export async function criarSolicitacaoAutomacao(input, options = {}) {
  const payload = montarPayloadSolicitacao(input);

  let data;
  try {
    data = await api.post("/planejamento/automacoes/solicitacoes", payload, {
      timeout: 20_000,
      signal: options.signal,
    });
  } catch (err) {
    if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") throw err;
    const mensagem = err?.response?.data?.mensagem;
    throw new Error(mensagem || "Não foi possível enviar a solicitação. Tente novamente.");
  }

  // repetido: a central já tinha esse job (não é erro) — sinaliza pro chamador decidir a UI.
  if (data?.repetido) {
    return {
      repetido: true,
      mensagem:
        data.motivo === "fiscal"
          ? "A central já tem uma coleta para este CNPJ e período (em andamento ou concluída)."
          : "Solicitação idêntica já enviada há instantes.",
    };
  }

  return {
    repetido: false,
    id: data?.solicitacao_id ?? data?.job_id ?? Date.now(),
    cnpj: payload.cnpj,
    razao_social: input.razaoSocial,
    tipo: payload.tipo,
    tipo_fluxo: payload.tipo_fluxo,
    sped_tipo: payload.sped_tipo,
    periodo_inicio: payload.periodo_inicio,
    periodo_fim: payload.periodo_fim,
    lote_dias: payload.lote_dias,
    status: data?.status ?? "pendente",
    criado_em: new Date().toISOString(),
    job_id: data?.job_id,
    execution_id: data?.execution_id,
  };
}
